import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Popover from 'tio-ui/components/popover';
import { LinkTo } from '@ember/routing';
import {
  PhBookOpen,
  PhBookOpenText,
  PhCurrencyCircleDollar,
  PhFileArrowUp,
  PhFirstAidKit,
  PhGear,
  PhGlobe,
  PhGraduationCap,
  PhGridNine,
  PhHandCoins,
  PhHeadset,
  PhHandHeart,
  PhHouse,
  PhLifebuoy,
  PhMegaphone,
  PhCheck,
  PhQuestionMark,
  PhRocketLaunch,
  PhScales,
  PhSignOut,
  PhStudent,
  PhTarget,
  PhUserCircle,
  PhUsersThree,
} from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { RoleViewExtension as RoleViewModel } from 'tio-data/data/resources/role-view.ext';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type BetaBrandService from 'tio-employee/services/beta-brand';

/**
 * Brand Updates 2026 side navigation.
 *
 * Source: Figma "Brand Updates 2026" → Side Navigation & Footer (section 47-528).
 * Employee frame `4-8566`, partner admin `4-8774`; dark variants `17-2806` and
 * `17-3023`. The annotations are numbered 1–8 in the design; each is quoted at
 * the code that implements it, here or in `styles/beta/side-nav.css`.
 *
 * This renders the **Employee** and **Partner Admin** variants — both live in
 * tio-employee and differ only by which groups the role can see. The third
 * variant (Internal Admin) is tio-admin's nav and is not implemented here; see
 * BETA_SIDE_NAV_GAPS.md (gap: internal-admin).
 *
 * Copy: existing `sidebar.*` keys are reused wherever the design's label matches
 * one. The six labels new to this design — Benefits, Loan Wellness, Loan
 * Repayment, Coaching, FAQs, Account Settings — have their own keys in all three
 * employee locales. They were briefly hardcoded English, which showed es-mx and
 * fr-ca users a half-translated rail.
 */

/**
 * DESTINATIONS ARE CARRIED OVER, NOT REDESIGNED.
 *
 * The Figma frame specifies labels, order and styling — it does not say where
 * anything points. Every row below therefore keeps the destination its previous
 * nav entry had, including Support's Zendesk-chat behaviour.
 *
 * ONE EXCEPTION, isolated here so it is easy to change: "FAQs" is a label with
 * no predecessor and no FAQ route in the app. Pointed at the nearest existing
 * surface pending design's answer.
 */
const FAQS_ROUTE = 'authenticated.questions-you-have.paying-for-college';

// ---------------------------------------------------------------------------
// Nav link — note 4 (sizing) + note 2 (selected state)
// ---------------------------------------------------------------------------

/**
 * A row in the rail.
 *
 * Renders a real `<LinkTo>` so the destination behaves like a link — middle-click
 * and cmd-click open a tab, the status bar shows where it goes, and the href is
 * copyable. It was a `<button>` calling `transitionTo`, which looked identical
 * and did none of that.
 *
 * The one exception is a row whose behaviour is an action rather than a
 * destination: Support opens Zendesk chat when the partner has it. That renders a
 * button, because it is one.
 */
interface BetaSideNavLinkSignature {
  Args: {
    /** Phosphor icon component for the row. */
    icon: unknown;
    /** Visible label. Callers pass a translated string. */
    label: string;
    /** In-app route this row goes to, and what the selected state matches on. */
    route: string;
    /**
     * Whether child routes count as selected. Defaults to true, so a section
     * landing stays lit while you are inside it; pass `false` for a row that
     * should only light on an exact match.
     */
    matchSubtree?: boolean;
    /**
     * Makes the row an action instead of a link — today only Support opening
     * Zendesk chat. `@route` is still used for the selected state.
     */
    onClick?: () => void;
  };
  Element: HTMLElement;
}

export class BetaSideNavLink extends Component<BetaSideNavLinkSignature> {
  @service declare router: RouterService;

  /**
   * Selected state. Read off `currentRouteName` rather than `router.isActive`
   * so it is autotracked — `isActive` is a method and does not invalidate on
   * its own when the route changes.
   */
  get isActive(): boolean {
    const current = this.router.currentRouteName;
    const route = this.args.route;

    if (!current || !route) {
      return false;
    }

    if (this.args.matchSubtree === false) {
      return current === route;
    }

    return current === route || current.startsWith(`${route}.`);
  }

  /**
   * Note 2: "Icon: Primary Purple 80, **Bold**". Phosphor's bold is an icon
   * weight, not a font-weight, so it cannot come from the stylesheet the way
   * the fill and text colour do.
   */
  get iconWeight(): 'bold' | 'regular' {
    return this.isActive ? 'bold' : 'regular';
  }

  <template>
    <li>
      {{#if @onClick}}
        <button
          type="button"
          class="tio-beta-nav__link {{if this.isActive 'tio-beta-nav__link--active'}}"
          aria-current={{if this.isActive "page"}}
          {{on "click" @onClick}}
          ...attributes
        >
          <@icon @size="20" @weight={{this.iconWeight}} class="tio-beta-nav__link-icon" />
          <span class="tio-beta-nav__link-label">{{@label}}</span>
        </button>
      {{else}}
        <LinkTo
          @route={{@route}}
          class="tio-beta-nav__link {{if this.isActive 'tio-beta-nav__link--active'}}"
          aria-current={{if this.isActive "page"}}
          ...attributes
        >
          <@icon @size="20" @weight={{this.iconWeight}} class="tio-beta-nav__link-icon" />
          <span class="tio-beta-nav__link-label">{{@label}}</span>
        </LinkTo>
      {{/if}}
    </li>
  </template>
}

// ---------------------------------------------------------------------------
// Section — note 3
// ---------------------------------------------------------------------------

interface BetaSideNavGroupSignature {
  Args: { title?: string };
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

export const BetaSideNavGroup: TOC<BetaSideNavGroupSignature> = <template>
  <div class="tio-beta-nav__group" ...attributes>
    {{#if @title}}
      <div class="tio-beta-nav__group-title">{{@title}}</div>
    {{/if}}
    <ul class="tio-beta-nav__items">
      {{yield}}
    </ul>
  </div>
</template>;

// ---------------------------------------------------------------------------
// Role row — carried over from the previous user menu
// ---------------------------------------------------------------------------

interface BetaSideNavRoleSignature {
  Args: {
    role: RoleViewModel;
    currentType?: string;
    onSelect: (role: RoleViewModel) => void;
  };
  Element: HTMLButtonElement;
}

class BetaSideNavRole extends Component<BetaSideNavRoleSignature> {
  get isCurrent(): boolean {
    return this.args.currentType === this.args.role.relationshipType;
  }

  get labelKey(): string {
    return `user-roles.${this.args.role.relationshipType.toLowerCase()}`;
  }

  select = () => this.args.onSelect(this.args.role);

  <template>
    <button
      type="button"
      class="tio-beta-nav__user-menu-item"
      data-test-beta-side-nav-role
      {{on "click" this.select}}
      ...attributes
    >
      {{#if this.isCurrent}}
        <PhCheck @size="20" />
      {{else}}
        <span class="tio-beta-nav__user-menu-spacer"></span>
      {{/if}}
      {{t this.labelKey}}
    </button>
  </template>
}

// ---------------------------------------------------------------------------
// User pill — note 6
// ---------------------------------------------------------------------------

/**
 * Note 6: "The User component includes a small avatar and the username on
 * file. This element is clickable and opens a small context menu where user
 * profile elements can be revealed. This currently includes: settings, logout."
 *
 * Note the wording — "currently includes" describes what the menu holds, it does
 * not forbid what is already there. The previous menu also carried a **role
 * switcher**, and it is the only way to change role anywhere in the app. It is
 * kept, shown only when the user actually has more than one active role, so a
 * single-role user sees exactly the two items the design draws.
 * See BETA_SIDE_NAV_GAPS.md (gap: role-switch).
 */
export class BetaSideNavUser extends Component {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  @tracked isMenuOpen = false;

  get person() {
    return this.sessionContext.user?.person;
  }

  get fullName(): string {
    const person = this.person;

    if (!person) {
      return '';
    }

    return [person.firstName, person.lastName].filter(Boolean).join(' ');
  }

  get initials(): string {
    return this.person?.initials ?? '';
  }

  /** Carried over from the previous menu — see the class comment. */
  get activeRoles() {
    return this.sessionContext.activeRoles;
  }

  get hasRoleChoice(): boolean {
    return !this.sessionContext.isFamilyMember && this.activeRoles.length > 1;
  }

  get currentRelationshipType(): string | undefined {
    return this.sessionContext.currentRole?.relationshipType;
  }

  setRole = (role: RoleViewModel) => {
    this.isMenuOpen = false;
    this.sessionContext.setCurrentRole(role);
  };

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  goTo = (route: string) => {
    this.isMenuOpen = false;
    this.router.transitionTo(route);
  };

  /** Note 6 names exactly two items: settings and logout. */
  goToAccountSettings = () => this.goTo('authenticated.profile');
  goToLogout = () => this.goTo('logout');

  <template>
    <div class="tio-beta-nav__user">
      <Popover @isOpen={{this.isMenuOpen}} @onClose={{this.closeMenu}} @placement="top">
        <:trigger as |p|>
          <button
            type="button"
            class="tio-beta-nav__user-pill"
            aria-haspopup="menu"
            aria-expanded={{if this.isMenuOpen "true" "false"}}
            data-test-beta-side-nav-user
            {{p.register}}
            {{on "click" this.toggleMenu}}
          >
            <span class="tio-beta-nav__user-avatar" aria-hidden="true">{{this.initials}}</span>
            <span class="tio-beta-nav__user-name">{{this.fullName}}</span>
          </button>
        </:trigger>
        <:body>
          {{! Deliberately NOT role="menu": that pattern promises managed focus and
              Arrow/Home/End handling, and Popover only handles dismissal. As a
              plain group of buttons it keeps normal tab order, which is honest
              about what it supports. }}
          <div class="tio-beta-nav__user-menu">
            <button
              type="button"
              class="tio-beta-nav__user-menu-item"
              data-test-beta-side-nav-account-settings
              {{on "click" this.goToAccountSettings}}
            >
              <PhGear @size="20" />
              {{t "sidebar.account_settings"}}
            </button>
            {{#if this.hasRoleChoice}}
              {{#each this.activeRoles as |role|}}
                <BetaSideNavRole
                  @role={{role}}
                  @currentType={{this.currentRelationshipType}}
                  @onSelect={{this.setRole}}
                />
              {{/each}}
            {{/if}}
            <button
              type="button"
              class="tio-beta-nav__user-menu-item"
              data-test-beta-side-nav-logout
              {{on "click" this.goToLogout}}
            >
              <PhSignOut @size="20" />
              {{t "header.user_menu.logout"}}
            </button>
          </div>
        </:body>
      </Popover>
    </div>
  </template>
}

// ---------------------------------------------------------------------------
// Nav
// ---------------------------------------------------------------------------

export interface BetaSideNavSignature {
  Args: {
    // ── Who the viewer is ─────────────────────────────────────────────────
    // These four overlap deliberately: a person can hold several at once, and
    // several rows are gated on a combination. `isTaOnly` is not a role but a
    // partner shape — a company whose only benefit is tuition assistance.

    /** Partner offers tuition assistance only, so the rail hides loan rows. */
    isTaOnly: boolean;
    /** Viewer is an enrolled employee, the audience for the Benefits group. */
    isParticipant: boolean;
    /** Viewer is a participant's family member; sees a narrower Benefits set. */
    isFamilyMember: boolean;

    // ── Which benefits the partner has ────────────────────────────────────
    // Partner-level switches. A benefit row needs BOTH its switch here and an
    // audience above — see `showLearningPartners` for why the pairing matters.

    /** Company has tuition assistance launched (`company.taEnabled`). */
    showTuitionAssistance: boolean;
    /** Show Loan Wellness. False for a D-5 wellness train. */
    showWellness: boolean;
    /** Company has PSLF and the employee has no rogue FEIN. */
    showPslf: boolean;
    /** Company has Secure Your Future (401k match). */
    showSyf: boolean;
    /** Company has income-driven repayment. */
    showIdr: boolean;
    /** Employee has a contribution plan whose cadence is not NEVER. */
    showSlr: boolean;
    /** Company has Learning Partners enabled (`companySettings.lpn`). */
    isLpnEnabled: boolean;

    // ── Administration — note 8 ───────────────────────────────────────────
    // Also overlapping. `isAdmin` is Tuition.io staff and outranks the rest;
    // the others are partner-side and are usually paired with a benefit switch
    // (an owner only sees the TA admin rows if the partner actually has TA).

    /** Tuition.io internal staff (TIO_ADMIN). Sees every Administration row. */
    isAdmin: boolean;
    /** Partner ACCOUNT_OWNER. */
    isOwner: boolean;
    /** Partner PSLF_ADMIN; sees Admin PSLF without needing owner. */
    isPslfAdmin: boolean;
    /** Partner EMPLOYEE_ADMIN. */
    isEmployeeAdmin: boolean;
    /** Holds any `TAS.Approver*` role; sees the approval queue. */
    isTasApprover: boolean;
    /** `taPartnerPaymentsReviewPage` feature flag, for the TA Payments row. */
    isTasPartnerPaymentsEnabled: boolean;

    // ── Support ───────────────────────────────────────────────────────────

    /** Partner has Zendesk chat, which makes Support an action, not a link. */
    isZendeskEnabled: boolean;
    /** Opens Zendesk chat. Only called when `isZendeskEnabled`. */
    onOpenChat: () => void;
  };
  Element: HTMLElement;
}

export default class BetaSideNav extends Component<BetaSideNavSignature> {
  @service declare preRegister: PreRegisterService;
  @service declare betaBrand: BetaBrandService;

  /** Set when the partner's logo asset fails to load, so the slot renders empty. */
  @tracked private logoFailed = false;

  /**
   * Note 1: "We are moving the Partner logo out of the header and into our
   * side navigation... we should limit Partner logos to 40px height."
   * The cap is enforced in CSS so it holds for any asset a partner uploads.
   */
  get partnerLogoUrl(): string | null {
    return this.preRegister.logo ?? null;
  }

  /**
   * A configured URL is not a loadable asset — a partner's logo can 404 or be
   * withdrawn. Note 1's empty slot is the intended logo-less state, so falling
   * back to it beats leaving the browser's broken-image icon in the rail.
   */
  get showPartnerLogo(): boolean {
    return Boolean(this.partnerLogoUrl) && !this.logoFailed;
  }

  handleLogoError = () => {
    this.logoFailed = true;
  };

  get partnerName(): string {
    return this.preRegister.companyName ?? 'Tuition.io';
  }

  get brand(): string {
    return this.betaBrand.brand;
  }

  // ── Administration gates, copied verbatim from the previous rail ────────
  get showAdministration(): boolean {
    const { isAdmin, isOwner, isPslfAdmin, isTasApprover, isEmployeeAdmin } = this.args;

    return Boolean(isAdmin || isOwner || isPslfAdmin || isTasApprover || isEmployeeAdmin);
  }

  get showAdminCore(): boolean {
    return Boolean(this.args.isAdmin || this.args.isOwner);
  }

  get showAdminPslf(): boolean {
    const { isAdmin, isOwner, showPslf, isPslfAdmin } = this.args;

    return Boolean(isAdmin || (isOwner && showPslf) || isPslfAdmin);
  }

  get showAdminTas(): boolean {
    const { isAdmin, isOwner, isEmployeeAdmin, showTuitionAssistance, isTasApprover } = this.args;

    return Boolean(
      isAdmin || ((isOwner || isEmployeeAdmin) && showTuitionAssistance) || isTasApprover
    );
  }

  /**
   * The legacy rail nested this row inside the TA-admin group, so that group's
   * gate applied on top of the payments flag. Flattening the rail dropped it,
   * which showed a TA destination to an employee admin at a partner with TA
   * switched off. `showAdminTas` is re-applied to restore the old audience.
   */
  get showAdminTasPayments(): boolean {
    const { isTasPartnerPaymentsEnabled, isAdmin, isOwner, isEmployeeAdmin } = this.args;

    return Boolean(
      this.showAdminTas && isTasPartnerPaymentsEnabled && (isAdmin || isOwner || isEmployeeAdmin)
    );
  }

  /**
   * Learning Partners was only ever a member-facing row — the legacy rail put it
   * inside the TA-only branch and the participant/family branch, never in an
   * admin one. Gating on the company flag alone showed it to admin-only logins.
   */
  get showLearningPartners(): boolean {
    return Boolean(this.args.isLpnEnabled && (this.isMember || this.isTaOnlyMember));
  }

  get showAllReports(): boolean {
    return Boolean(this.args.isAdmin || this.args.isOwner || this.args.isEmployeeAdmin);
  }

  // ── Benefit gates ───────────────────────────────────────────────────────
  get isTaOnlyMember(): boolean {
    return Boolean(this.args.isTaOnly && this.args.showTuitionAssistance);
  }

  get isMember(): boolean {
    return Boolean(this.args.isParticipant || this.args.isFamilyMember);
  }

  /** The previous rail hid these from family members. */
  get showMemberOnly(): boolean {
    return this.isMember && !this.args.isFamilyMember;
  }

  // One getter per design-named row, so the template reads in design order.
  get showTuitionAssistanceRow(): boolean {
    return Boolean(this.isTaOnlyMember || (this.showMemberOnly && this.args.showTuitionAssistance));
  }

  get showWellnessRow(): boolean {
    return this.showMemberOnly && Boolean(this.args.showWellness);
  }

  get showPslfRow(): boolean {
    return this.showMemberOnly && Boolean(this.args.showPslf);
  }

  get showSlrRow(): boolean {
    return this.isMember && Boolean(this.args.showSlr);
  }

  get showSyfRow(): boolean {
    return this.showMemberOnly && Boolean(this.args.showSyf);
  }

  get showIdrRow(): boolean {
    return this.showMemberOnly && Boolean(this.args.showIdr);
  }

  <template>
    <nav
      class="tio-beta-nav"
      data-beta-brand={{this.brand}}
      aria-label={{t "sidebar.home"}}
      data-test-beta-side-nav
      ...attributes
    >
      <div class="tio-beta-nav__top">

        {{! ── Note 1 — partner logo, capped at 40px high ──
             The Tuition.io mark used to stand in here when a partner had no
             logo. Footer note 1 moves that mark to the footer ("out of the side
             nav and into our new footer component"), so the slot now shows the
             partner logo or nothing. Its 88px height is on the container, so a
             logo-less partner gets the same spacing rather than a shifted rail.
             Whether that gap wants filling is a question for design. }}
        <div class="tio-beta-nav__logo">
          {{#if this.showPartnerLogo}}
            <img
              src={{this.partnerLogoUrl}}
              alt={{this.partnerName}}
              class="tio-beta-nav__logo-img"
              {{on "error" this.handleLogoError}}
              data-test-beta-side-nav-logo
            />
          {{/if}}
        </div>

        <div class="tio-beta-nav__groups">

          {{! Home sits on its own, above the labelled groups. }}
          <BetaSideNavGroup>
            <BetaSideNavLink
              @route="authenticated.dashboard"
              @icon={{PhHouse}}
              @label={{t "sidebar.home"}}
              @matchSubtree={{false}}
              data-test-beta-side-nav-home
            />
          </BetaSideNavGroup>

          {{! ── Note 8 — Administration ──
               "These are links both internal and Partner Admin will need."
               The design shows four rows; the previous rail exposed six behind
               finer-grained gates. All six are kept — the gates are copied
               verbatim so nobody's nav loses an entry they had. }}
          {{#if this.showAdministration}}
            <BetaSideNavGroup @title={{t "sidebar.administration"}}>
              {{#if this.showAdminCore}}
                <BetaSideNavLink
                  @route="authenticated.admin.employees"
                  @icon={{PhUsersThree}}
                  @label={{t "sidebar.employees"}}
                  data-test-beta-side-nav-admin-employees
                />
                <BetaSideNavLink
                  @route="authenticated.admin.eligibility-files"
                  @icon={{PhFileArrowUp}}
                  @label={{t "sidebar.eligibility_files"}}
                  data-test-beta-side-nav-admin-eligibility-files
                />
              {{/if}}
              {{#if this.showAdminTas}}
                <BetaSideNavLink
                  @route="authenticated.admin.tas.approval-queue"
                  @icon={{PhStudent}}
                  @label={{t "sidebar.approval_queue"}}
                  data-test-beta-side-nav-admin-tas-approval-queue
                />
              {{/if}}
              {{#if this.showAdminPslf}}
                <BetaSideNavLink
                  @route="authenticated.admin.pslf.forms"
                  @icon={{PhHandHeart}}
                  @label={{t "sidebar.admin_pslf"}}
                  data-test-beta-side-nav-admin-pslf
                />
              {{/if}}

              {{! Below: rows the previous rail exposed that the design does not
                  name. Kept so no admin loses a destination. }}
              {{#if this.showAdminCore}}
                <BetaSideNavLink
                  @route="authenticated.admin.reports"
                  @icon={{PhGridNine}}
                  @label={{t "common.reporting.default"}}
                  data-test-beta-side-nav-admin-reports
                />
              {{/if}}
              {{#if this.showAdminTasPayments}}
                <BetaSideNavLink
                  @route="authenticated.admin.tuition-assistance.payments"
                  @icon={{PhStudent}}
                  @label={{t "sidebar.payments"}}
                  data-test-beta-side-nav-admin-tuition-assistance-payments
                />
              {{/if}}
              {{#if this.showAllReports}}
                <BetaSideNavLink
                  @route="authenticated.admin.reporting.index"
                  @icon={{PhGridNine}}
                  @label={{t "reports.all_reports_link"}}
                  data-test-beta-side-nav-admin-tuition-assistance-reports
                />
              {{/if}}
            </BetaSideNavGroup>
          {{/if}}

          {{! ── Benefits ──
               The design names five rows, in this order. Everything below the
               marked divide is a destination the previous rail exposed that the
               design does not name; kept so no partner configuration loses a
               link. }}
          <BetaSideNavGroup @title={{t "sidebar.benefits"}}>
            {{#if this.showTuitionAssistanceRow}}
              <BetaSideNavLink
                @route="authenticated.tas.dashboard"
                @icon={{PhGraduationCap}}
                @label={{t "sidebar.tuition_assistance"}}
                data-test-beta-side-nav-employee-primary-tuition-assistance
              />
            {{/if}}
            {{#if this.showWellnessRow}}
              <BetaSideNavLink
                @route="authenticated.wellness-insights"
                @icon={{PhFirstAidKit}}
                @label={{t "sidebar.loan_wellness"}}
                data-test-beta-side-nav-loan-insights
              />
            {{/if}}
            {{#if this.showPslfRow}}
              <BetaSideNavLink
                @route="authenticated.pslf.dashboard"
                @icon={{PhLifebuoy}}
                @label={{t "sidebar.loan_forgiveness"}}
                data-test-beta-side-nav-pslf
              />
            {{/if}}
            {{#if this.showSlrRow}}
              <BetaSideNavLink
                @route="authenticated.slr.dashboard"
                @icon={{PhHandCoins}}
                @label={{t "sidebar.loan_repayment"}}
                data-test-beta-side-nav-employee-employer-contributions
              />
            {{/if}}
            {{#if this.showLearningPartners}}
              <BetaSideNavLink
                @route="authenticated.lpn.index"
                @icon={{PhGlobe}}
                @label={{t "sidebar.learning_partners"}}
              />
            {{/if}}

            {{! ── not named by the design ── }}
            {{#if this.isMember}}
              <BetaSideNavLink
                @route="authenticated.account-activity.accounts"
                @icon={{PhUserCircle}}
                @label={{t "sidebar.loan_summary"}}
                data-test-beta-side-nav-employee-student-loan-summary
              />
            {{/if}}
            {{#if this.showSyfRow}}
              <BetaSideNavLink
                @route="authenticated.syf.dashboard"
                @icon={{PhRocketLaunch}}
                @label={{t "sidebar.secure_your_future"}}
                data-test-beta-side-nav-employee-primary-secure-your-future
              />
            {{/if}}
            {{#if this.showIdrRow}}
              <BetaSideNavLink
                @route="authenticated.idr.dashboard"
                @icon={{PhCurrencyCircleDollar}}
                @label={{t "sidebar.income_driven_repayment"}}
                data-test-beta-side-nav-employee-primary-income-driven-repayment
              />
            {{/if}}
          </BetaSideNavGroup>

          {{! ── Resources ── design's four first, then unnamed extras. }}
          <BetaSideNavGroup @title={{t "sidebar.resources"}}>
            {{#if this.isMember}}
              <BetaSideNavLink
                @route="authenticated.repaying-student-debt.coaching"
                @icon={{PhMegaphone}}
                @label={{t "sidebar.coaching"}}
              />
            {{/if}}
            {{#if this.showMemberOnly}}
              <BetaSideNavLink
                @route={{FAQS_ROUTE}}
                @icon={{PhBookOpenText}}
                @label={{t "sidebar.faqs"}}
              />
            {{/if}}
            {{#if @isZendeskEnabled}}
              <BetaSideNavLink
                @route="authenticated.contact-us"
                @icon={{PhHeadset}}
                @label={{t "sidebar.support"}}
                @onClick={{@onOpenChat}}
              />
            {{else}}
              <BetaSideNavLink
                @route="authenticated.contact-us"
                @icon={{PhHeadset}}
                @label={{t "sidebar.support"}}
              />
            {{/if}}
            <BetaSideNavLink
              @route="authenticated.contact-us"
              @icon={{PhQuestionMark}}
              @label={{t "sidebar.help"}}
            />

            {{! ── not named by the design ──
                 The forgiveness GUIDE at
                 `repaying-student-debt.loan-forgiveness.index` used to sit here.
                 It shares the `sidebar.loan_forgiveness` label with the PSLF
                 dashboard in Benefits above, which the old nav got away with
                 because it was nested under "Repaying Debt" — this rail is flat,
                 so the same words appeared twice with different destinations.
                 Dropped rather than relabelled: inventing copy is design's call.
                 See BETA_SIDE_NAV_GAPS.md (gap: route-coverage). }}
            {{#if this.isMember}}
              <BetaSideNavLink
                @route="authenticated.repaying-student-debt.repayment-strategy-finder"
                @icon={{PhTarget}}
                @label={{t "sidebar.strategy_finder"}}
              />
            {{/if}}
            {{#if this.showMemberOnly}}
              <BetaSideNavLink
                @route="authenticated.repaying-student-debt.repayment-options"
                @icon={{PhScales}}
                @label={{t "sidebar.consolidation_refinancing"}}
              />
              {{#unless @showTuitionAssistance}}
                <BetaSideNavLink
                  @route="authenticated.college-planning-mentors"
                  @icon={{PhUsersThree}}
                  @label={{t "sidebar.education_mentors"}}
                />
              {{/unless}}
              <BetaSideNavLink
                @route="authenticated.questions-you-have.repayment-plans"
                @icon={{PhBookOpen}}
                @label={{t "sidebar.repayment_plans"}}
              />
              <BetaSideNavLink
                @route="authenticated.questions-you-have.glossary"
                @icon={{PhBookOpen}}
                @label={{t "sidebar.loan_glossary"}}
              />
            {{/if}}
          </BetaSideNavGroup>

        </div>
      </div>

      {{! ── Note 6 — user pill, pinned to the bottom ── }}
      <BetaSideNavUser />
    </nav>
  </template>
}
